import type { Item, Level, Room, Vec2 } from '../hp/types';
import { contains } from './edit';
import { itemFootprint } from './footprint';
import { measuredFootprint, modelBoundsVersion } from './modelBounds';
import { inWall, WALK_BODY_RADIUS, WALK_MAX_STEP } from './walkCollision';

/**
 * Stairwells: a flight of stairs that reaches the floor above leaves a hole in that floor, and in the ceiling it passes
 * through, the size of its footprint. The hole moves, turns, grows and goes with the flight, so it is worked out from the
 * flights every time and nothing of it is written into the project.
 *
 * The app has no such rule: its holes are items of their own a person places by hand (the «Openings» gap 8524 on a room's
 * Floor/Ceiling, kept in pairs across two floors by FloorCeilingSyncController), and its walk climbs to the floor above
 * only through one (StairTransitionInputBuilder). Here a flight that is tall enough is its own stairwell.
 */

/** The app's IsInclinedStairs() (FurnitureInfo.cs): the flights its walk climbs. A ladder, a round stair or a decal is not one. */
const FLIGHT_TAGS: ReadonlySet<string> = new Set(['Stairs', 'StairsOpening', 'CornerStairs', 'StairsStraight90']);

/** A flight the walk climbs, standing on the floor, whatever its height. */
export function isFlight(item: Item): boolean {
  return item.placement === 'floor' && (item.tags ?? []).some(tag => FLIGHT_TAGS.has(tag));
}

/**
 * A flight that reaches the floor above: it stands on the floor, within a step of it, and its top comes to within a step
 * of the next floor, so the walk can step off it there. A flight taller than the floor still leads up; a few steps to a
 * podium, a decal of a stairwell (1 cm) or a ladder do not.
 */
export function leadsUp(item: Item, level: Pick<Level, 'height'>): boolean {
  return isFlight(item) && item.elevation <= WALK_MAX_STEP && item.elevation + item.size.h >= level.height - WALK_MAX_STEP;
}

/** A hole in a floor over a flight from the floor below, and in the ceiling that flight passes through. */
export interface Stairwell {
  /** The flight's item id on the floor below. */
  id: string;
  item: Item;
  /** Its footprint, counter-clockwise (positive area in x·z). */
  polygon: Vec2[];
  centre: Vec2;
}

const flights = new WeakMap<Level, { version: number; wells: Stairwell[] }>();

/**
 * The flights of `level` that reach the floor above it, as holes; worked out once per level, and again when a model has
 * been measured. A hole covers the flight as it is drawn: its model's footprint once the model is known
 * (`measuredFootprint`), the catalog box round its position until then.
 */
function flightsUp(level: Level): Stairwell[] {
  const version = modelBoundsVersion();
  let cached = flights.get(level);
  if (!cached || cached.version !== version) {
    const wells = level.items.filter(item => leadsUp(item, level)).map(item => {
      const measured = measuredFootprint(item);
      const corners = itemFootprint(item, measured && new Map([[item.id, measured]]));
      const centre = { x: corners.reduce((sum, p) => sum + p.x, 0) / 4, z: corners.reduce((sum, p) => sum + p.z, 0) / 4 };
      return { id: item.id, item, polygon: signedArea(corners) >= 0 ? corners : [...corners].reverse(), centre };
    });
    flights.set(level, cached = { version, wells });
  }
  return cached.wells;
}

/** The holes in the ceilings of `levels[index]`: its own flights that lead up, when there is a floor above to lead to. */
export function wellsUp(levels: readonly Level[], index: number): Stairwell[] {
  const level = levels[index];
  return level && levels[index + 1] ? flightsUp(level) : [];
}

/** The holes in the floor of `levels[index]`: the flights of the floor below that lead up to it. */
export function wellsDown(levels: readonly Level[], index: number): Stairwell[] {
  return index > 0 ? wellsUp(levels, index - 1) : [];
}

/** A way off a floor by its stairs: through `well` to floor `to`. */
export interface Stairway { direction: 'up' | 'down'; to: number; well: Stairwell }

/**
 * Where the stairs of `levels[index]` lead, as the walk climbs them (`Viewer.climbFloors`): down through the holes in its
 * floor and up through its own flights, each only to a floor with a room at the hole and somewhere to stand in it
 * (`stairLanding`); the ways down first.
 */
export function stairwaysFrom(levels: readonly Level[], index: number): Stairway[] {
  const below = levels[index - 1], above = levels[index + 1];
  const down = below ? wellsDown(levels, index).map(well => ({ direction: 'down' as const, to: index - 1, well })) : [];
  const up = above ? wellsUp(levels, index).map(well => ({ direction: 'up' as const, to: index + 1, well })) : [];
  return [...down, ...up].filter(way => stairLanding(levels, way));
}

/** An end of a flight: the middle of its footprint's edge there, half that edge, and the way out of the footprint over it. */
export interface FlightEnd { mid: Vec2; half: number; out: Vec2 }

/** The two ends of a flight's footprint: its two shorter opposite edges. */
export function flightEnds(well: Pick<Stairwell, 'polygon'>): [FlightEnd, FlightEnd] | undefined {
  const p = well.polygon;
  if (p.length !== 4) return undefined;
  const length = (i: number) => Math.hypot(p[(i + 1) % 4].x - p[i].x, p[(i + 1) % 4].z - p[i].z);
  const edge = (i: number): FlightEnd => {
    const a = p[i], b = p[(i + 1) % 4], l = length(i) || 1;
    return { mid: { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 }, half: l / 2, out: { x: (b.z - a.z) / l, z: -(b.x - a.x) / l } };
  };
  return length(0) + length(2) <= length(1) + length(3) ? [edge(0), edge(2)] : [edge(1), edge(3)];
}

/** Where a walker put down on a floor by its stairs stands, heading (sin yaw, cos yaw). */
export interface StairLanding { x: number; z: number; yaw: number }

const LANDING_GAP = WALK_BODY_RADIUS + .05, LANDING_STEP = .25;

/**
 * Where the walk stands on floor `way.to` when the stairs take it there at once: in the room the hole opens into, out of
 * its walls (`inWall`), a body clear of the flight and of every hole in that floor, facing away from the stairs. Past an
 * end of the flight first, the end nearest `toward` before the other; then beside it, stepping back along it from each
 * end; then the point of that room nearest the first end, or its middle. None where the room leaves nowhere to stand.
 */
export function stairLanding(levels: readonly Level[], way: Stairway, toward?: Vec2): StairLanding | undefined {
  const target = levels[way.to], room = target && wellRoom(target.rooms, way.well), ends = flightEnds(way.well);
  if (!room || !ends) return undefined;
  const holes = [way.well, ...wellsDown(levels, way.to)];
  const at = (p: Vec2, heading: Vec2): StairLanding | undefined =>
    contains(room.polygon, p) && holes.every(hole => depthInWell(hole, p) <= -WALK_BODY_RADIUS) && !inWall(target, p)
      ? { x: p.x, z: p.z, yaw: Math.atan2(heading.x, heading.z) } : undefined;
  const near = (end: FlightEnd) => toward ? Math.hypot(end.mid.x - toward.x, end.mid.z - toward.z) : 0;
  const [first, second] = near(ends[1]) < near(ends[0]) ? [ends[1], ends[0]] : ends;
  const span = Math.hypot(first.mid.x - second.mid.x, first.mid.z - second.mid.z);
  const past = (end: FlightEnd) => at({ x: end.mid.x + end.out.x * LANDING_GAP, z: end.mid.z + end.out.z * LANDING_GAP }, end.out);
  const beside = (end: FlightEnd) => {
    for (let t = LANDING_STEP; t <= span / 2 + 1e-9; t += LANDING_STEP) {
      for (const side of [1, -1]) {
        const across = { x: -end.out.z * side, z: end.out.x * side }, reach = end.half + LANDING_GAP;
        const spot = at({ x: end.mid.x - end.out.x * t + across.x * reach, z: end.mid.z - end.out.z * t + across.z * reach }, across);
        if (spot) return spot;
      }
    }
    return undefined;
  };
  const found = past(first) ?? past(second) ?? beside(first) ?? beside(second);
  if (found) return found;
  const xs = room.polygon.map(p => p.x), zs = room.polygon.map(p => p.z);
  const middle = { x: xs.reduce((sum, x) => sum + x, 0) / xs.length, z: zs.reduce((sum, z) => sum + z, 0) / zs.length };
  let best: StairLanding | undefined, nearest = Infinity;
  for (let x = Math.min(...xs) + LANDING_STEP / 2; x < Math.max(...xs); x += LANDING_STEP) {
    for (let z = Math.min(...zs) + LANDING_STEP / 2; z < Math.max(...zs); z += LANDING_STEP) {
      const distance = Math.hypot(x - first.mid.x, z - first.mid.z);
      const spot = distance < nearest ? at({ x, z }, { x: x - way.well.centre.x, z: z - way.well.centre.z }) : undefined;
      if (spot) { best = spot; nearest = distance; }
    }
  }
  return best ?? at(middle, { x: middle.x - way.well.centre.x, z: middle.z - way.well.centre.z });
}

/** A point inside the counter-clockwise convex `polygon` put just past its nearest edge, `margin` beyond it; one outside stays. */
export function stepOutOf(polygon: readonly Vec2[], point: Vec2, margin: number): Vec2 {
  if (!insideConvex(polygon, point)) return point;
  let best: { x: number; z: number; depth: number } | null = null;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length], length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-6) continue;
    // The inside is to the left of each edge, the way out to the right.
    const out = { x: (b.z - a.z) / length, z: -(b.x - a.x) / length };
    const depth = (point.x - a.x) * -out.x + (point.z - a.z) * -out.z;
    if (!best || depth < best.depth) best = { x: out.x, z: out.z, depth };
  }
  if (!best) return point;
  const step = best.depth + margin;
  return { x: point.x + best.x * step, z: point.z + best.z * step };
}

/** The hole a plan point is in, if any. */
export function wellAt(wells: readonly Stairwell[], point: Vec2): Stairwell | undefined {
  return wells.find(well => insideConvex(well.polygon, point));
}

/** How far inside a hole a point is: its distance to the nearest edge; negative outside. */
export function depthInWell(well: Pick<Stairwell, 'polygon'>, point: Vec2): number {
  let depth = Infinity;
  for (let i = 0; i < well.polygon.length; i++) {
    const a = well.polygon[i], b = well.polygon[(i + 1) % well.polygon.length], length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-9) continue;
    depth = Math.min(depth, ((b.x - a.x) * (point.z - a.z) - (b.z - a.z) * (point.x - a.x)) / length);
  }
  return depth;
}

/** The room a hole is cut in: the tightest one round its centre, as the app keeps a floor opening on the room it lies in. */
export function wellRoom<R extends Room>(rooms: readonly R[], well: Pick<Stairwell, 'centre'>): R | undefined {
  let best: R | undefined;
  for (const room of rooms) if (room.polygon.length >= 3 && contains(room.polygon, well.centre) && (!best || room.area < best.area)) best = room;
  return best;
}

/** Holes as plain numbers, to tell whether they changed. */
export function wellsKey(wells: readonly Stairwell[]): string {
  return wells.map(well => well.polygon.map(p => `${p.x.toFixed(4)},${p.z.toFixed(4)}`).join(' ')).join('|');
}

function signedArea(polygon: readonly Vec2[]): number {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) { const a = polygon[i], b = polygon[(i + 1) % polygon.length]; sum += a.x * b.z - a.z * b.x; }
  return sum / 2;
}

/** Inside or on the edge of a counter-clockwise convex polygon. */
function insideConvex(polygon: readonly Vec2[], point: Vec2): boolean {
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i], b = polygon[(i + 1) % polygon.length];
    if ((b.x - a.x) * (point.z - a.z) - (b.z - a.z) * (point.x - a.x) < -1e-9) return false;
  }
  return polygon.length >= 3;
}

/** The part of `polygon` on one side of the line through a and b (Sutherland–Hodgman against one edge). */
function clip(polygon: readonly Vec2[], a: Vec2, b: Vec2, keep: 1 | -1): Vec2[] {
  const side = (p: Vec2) => keep * ((b.x - a.x) * (p.z - a.z) - (b.z - a.z) * (p.x - a.x));
  const out: Vec2[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i], q = polygon[(i + 1) % polygon.length], sp = side(p), sq = side(q);
    if (sp >= 0) out.push(p);
    if ((sp > 0 && sq < 0) || (sp < 0 && sq > 0)) { const t = sp / (sp - sq); out.push({ x: p.x + t * (q.x - p.x), z: p.z + t * (q.z - p.z) }); }
  }
  return out;
}

/**
 * `polygon` without the counter-clockwise convex `hole`: the pieces that cover the rest of it, cut off along the hole's
 * edges one after another. A hole that misses the polygon leaves it whole, one that covers it leaves nothing. A room may
 * be concave; a piece then runs along the cut line to its other part, which triangulates as one.
 */
export function subtractConvex(polygon: readonly Vec2[], hole: readonly Vec2[]): Vec2[][] {
  const pieces: Vec2[][] = [];
  let rest: Vec2[] = [...polygon];
  for (let i = 0; i < hole.length && rest.length >= 3; i++) {
    const a = hole[i], b = hole[(i + 1) % hole.length];
    const outside = clip(rest, a, b, -1);
    if (outside.length >= 3 && Math.abs(signedArea(outside)) > 1e-9) pieces.push(outside);
    rest = clip(rest, a, b, 1);
  }
  return pieces;
}

/**
 * A room's floor or ceiling without the counter-clockwise convex `holes` in it, as pieces; the polygon itself, alone,
 * when none of them meets it.
 */
export function withoutHoles(polygon: readonly Vec2[], holes: readonly (readonly Vec2[])[]): (readonly Vec2[])[] {
  let pieces: (readonly Vec2[])[] = [polygon];
  for (const hole of holes) {
    if (!overlaps(polygon, hole)) continue;
    pieces = pieces.flatMap(piece => subtractConvex(piece, hole));
  }
  return pieces;
}

function overlaps(a: readonly Vec2[], b: readonly Vec2[]): boolean {
  const box = (p: readonly Vec2[]) => ({ x0: Math.min(...p.map(v => v.x)), x1: Math.max(...p.map(v => v.x)), z0: Math.min(...p.map(v => v.z)), z1: Math.max(...p.map(v => v.z)) });
  const p = box(a), q = box(b);
  return p.x0 < q.x1 && q.x0 < p.x1 && p.z0 < q.z1 && q.z0 < p.z1;
}
